import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { passengerCMSApi, post, remove } from "~/Services";
import { eramanHost, passengerHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import { renderActionsColumn, renderDateColumn, renderPublishedColumnStatus } from "~/Utils/TableRenderFunction";
import { useAppCardsDocumentDetails } from "./appcard.hook";

export const AppCard = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [categoriesData, setCategoriesData] = useState<any[]>([]);
  const [selectedPublished, setSelectedPublished] = useState<any>("");

  const [currentAirport] = useLocalStorage("currentAirport", null);

  const publishedData: TypeData[] = [
    {
      label: "Yes",
      value: "True",
    },
    {
      label: "No",
      value: "False",
    },
  ];

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");

  const {
    data: drilldown,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchDocument,
  } = useAppCardsDocumentDetails(id || "");

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    setSelectedPublished("");
    setSearchValue("");
    setCurrentPage(0);
  }, [query.get("activeTab")]);

  useEffect(() => {
    const safeData = Array.isArray(data) ? data : [];
    let filteredData = [...safeData];

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filteredData = filteredData.filter(
        (item: any) =>
          String(item.cardNameEn || "")
            .toLowerCase()
            .includes(searchLower)
        // ||
        // String(item.field_ranking || "")
        //   .toLowerCase()
        //   .includes(searchLower)
      );
    }

    if (selectedPublished !== "") {
      filteredData = filteredData.filter((item: any) => item.published === selectedPublished);
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);

  const getRecords = () => {
    setIsLoading(true);
    const url = `/api/get-mobile-app-cards/all-fields?airport=${currentAirport}`;

    passengerCMSApi
      .get(url)
      .then((res: any) => {
        if (res?.data) {
          const responseData = res.data;
          setData(responseData);
          setTotalRecords(responseData.length);
        } else {
          setData([]);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setData([]);
        setIsLoading(false);
      });
  };
  useEffect(() => {
    getRecords();
  }, []);

  const handleDelete = async (id: string) => {
    remove(`${eramanHost}/api/all-brands/${id}`)
      .then((res) => {
        getRecords();
      })
      .catch((err) => {});
  };

  const columns: TableColumnsType = [
    {
      title: "Card Name",
      dataIndex: "cardNameEn",
      key: "cardNameEn",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "cardNameEn"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "cardNameEn"
            ? "descend"
            : undefined,
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: renderPublishedColumnStatus,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "created_at"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "created_at"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      // render: renderDateColumn,
      render: (value: any, record: any) => {
        const created = record?.created_at ? moment(record.created_at).format("YYYY-MM-DD HH:mm:ss") : null;
        const updated = value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : null;

        // if both same -> show null for updated
        if (created && updated && created === updated) {
          return null; // you can also return "-" or <Typography.Text>-</Typography.Text>
        }

        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "changed"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "changed"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/passenger/mobileapp/cards/add?editId=${record.id}`);
          },
          undefined,
          setDeleteModalVisible,
          "users"
        ),
    },
  ];

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";

    if (sortOrder === "asc") {
      setSortOrder("desc");
      order = "desc";
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
      order = "asc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);
    const tempData = [...sortedData];
    setSortedData(tempData.sort((a, b) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action: action,
      method: method,
      module: "Mobile App",
      subModule: "appCardsListContent",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      reviewNotes: reviewNotes,
      airportName: currentAirport,
      appList: [
        {
          appName: "PASSENGER_ECMS",
          apiUrl: apiUrl,
        },
      ],
      payload: data,
      status: status,
    };

    post("/management/publish", payload)
      .then((response: any) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        if (response.status === "OK" && status == "PENDING") {
          navigate("/dedicated/status-information?activeTab=pending");
        }
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${passengerHost}/api/get-mobile-app-cards/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {}
  }

  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={
          currentPageData
            ? currentPageData?.map((d: any) => {
                return {
                  ...d,
                  name: decodeHTML(d.name),
                };
              })
            : []
        }
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={10}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search Name"}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          // onChange={(curPage) => {
          //   setCurrentPage(curPage - 1);
          // }}
        />
      </div>

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "appCardsListContent") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteRequest}
        defaultReviewNotes="Kindly review this Mobile App"
      />
    </>
  );
};

export function handleSort(a: any, b: any, order: string | undefined, sortBy: string) {
  if (sortBy === "field_faqs_question") {
    const numberA = extractNumber(a[sortBy]);
    const numberB = extractNumber(b[sortBy]);

    if (order === "desc") {
      return numberA < numberB ? 1 : -1;
    } else if (order === "asc") {
      return numberA > numberB ? 1 : -1;
    } else {
      return 0;
    }
  }
  if (sortBy === "name" || sortBy === "field_category_id") {
    const valueA = a[sortBy] ? String(a[sortBy]).toLowerCase() : "";
    const valueB = b[sortBy] ? String(b[sortBy]).toLowerCase() : "";

    if (order === "desc") {
      return valueA < valueB ? 1 : -1;
    } else if (order === "asc") {
      return valueA > valueB ? 1 : -1;
    } else {
      return 0;
    }
  }
  if (order === "desc") {
    return a[sortBy] < b[sortBy] ? 1 : -1;
  } else if (order === "asc") {
    return a[sortBy] > b[sortBy] ? 1 : -1;
  } else {
    return 0;
  }
}

export function extractNumber(question: string): number {
  const match = question.match(/Q(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
